import { action, makeObservable } from 'mobx';
import socketIo from '../socketIo';
import * as utils from '../../helpers/utils';

class SendingEvents {
  constructor(rootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
  }

  @action socketEmitGetSearchResult = (resultCount, filterWord, contactTypeIdsToSearch) => {
    socketIo.locationSocket.emit('client:getSearchResult', {
      resultCount,
      filterWord,
      contactTypeIdsToSearch,
    });
  };

  @action socketEmitRoom = (channelData, batchSize) => {
    if (this.rootStore.commonStore.fullModeChatInput) {
      const settings = this.rootStore.settingsStore.getLoggedUserSettings;
      const loggedUser = this.rootStore.commonStore.getLoggedUser;
      this.socketEmitContactStoppedTyping({
        channelId: settings?.SelectedChannel?._id,
        contactId: loggedUser?._id,
      });
    }

    this.rootStore.commonStore.setMainComponentOpen(null);
    this.rootStore.commonStore.setFullModeChatInput('');
    this.rootStore.commonStore.setShowMessageLoadScreen(true);

    socketIo.locationSocket.emit('client:room', {
      channelData,
      batchSize,
    });
  };

  @action socketEmitEditContactInfo = (Contact) => {
    socketIo.locationSocket.emit('client:editContactInfo', {
      Contact,
    });
  };

  @action socketEmitMessage = (messageObject, channelId, acceptedFiles, filesData) => {
    socketIo.locationSocket.emit(
      'client:message',
      messageObject,
      channelId,
      acceptedFiles,
      filesData
    );
  };

  @action socketEmitLoadMoreMessages = (selectedChannel, numberOfMessages, nextBatchSize) => {
    socketIo.locationSocket.emit('client:loadMoreMessages', {
      selectedChannel,
      numberOfMessages,
      nextBatchSize,
    });
  };

  @action socketEmitToggleCompactView = () => {
    socketIo.locationSocket.emit('client:toggleCompactView');
  };

  @action socketEmitToggleMuteChatSounds = () => {
    this.rootStore.settingsStore.setToggleMuteSounds();
    this.rootStore.commonStore.setIgnoreSettingsEntries(
      this.rootStore.commonStore.ignoreSettingsEntries + 1
    );

    socketIo.locationSocket.emit('client:toggleMuteChatSounds');
  };

  @action socketEmitToggleIsWebchatEnabled = () => {
    socketIo.locationSocket.emit('client:toggleIsWebchatEnabled');
  };

  @action socketEmitToggleIsAutoCloseWebChatEnabled = (autoCloseWebChatTimeInHours) => {
    socketIo.locationSocket.emit('client:toggleIsAutoCloseWebChatEnabled', {
      autoCloseWebChatTimeInHours,
    });
  };

  @action socketEmitTogglePersistentNotifications = () => {
    this.rootStore.settingsStore.setTogglePersistentNotifications();
    this.rootStore.commonStore.setIgnoreSettingsEntries(
      this.rootStore.commonStore.ignoreSettingsEntries + 1
    );

    this.rootStore.settingsStore.setClearSnackbarNotificationMessages();

    socketIo.locationSocket.emit('client:togglePersistentNotifications');
  };

  @action socketEmitToggleShowHiddenMessages = () => {
    this.rootStore.settingsStore.setToggleShowHiddenMessages();
    this.rootStore.commonStore.setIgnoreSettingsEntries(
      this.rootStore.commonStore.ignoreSettingsEntries + 1
    );

    socketIo.locationSocket.emit('client:toggleShowHiddenMessages');
  };

  @action socketEmitToggleSingleMessageVisibility = (messageId) => {
    socketIo.locationSocket.emit('client:toggleSingleMessageVisibility', messageId);
  };

  @action socketEmitLoadMoreChannels = (
    clientTotalNumberOfChannels,
    nextBatchSize,
    channelTypeIdsToSearch,
    filters
  ) => {
    socketIo.locationSocket.emit('client:loadMoreChannels', {
      clientTotalNumberOfChannels,
      nextBatchSize,
      channelTypeIdsToSearch,
      filters,
    });
  };

  @action socketEmitSnoozeNotificationsUntil = (timePeriods) => {
    if (
      !this.rootStore.settingsStore.getLoggedUserSettings.SnoozeNotificationsUntil ||
      new Date() >=
        new Date(this.rootStore.settingsStore.getLoggedUserSettings.SnoozeNotificationsUntil)
    ) {
      const snoozeUntil = new Date();

      snoozeUntil.setHours(
        snoozeUntil.getHours() + (timePeriods ? Number(timePeriods.split(':')[0]) : 0)
      );
      snoozeUntil.setMinutes(
        snoozeUntil.getMinutes() + (timePeriods ? Number(timePeriods.split(':')[1]) : 30)
      );
      snoozeUntil.setSeconds(0);

      this.rootStore.settingsStore.getLoggedUserSettings.SnoozeNotificationsUntil = snoozeUntil;
    } else {
      this.rootStore.settingsStore.getLoggedUserSettings.SnoozeNotificationsUntil = null;
    }

    this.rootStore.commonStore.setIgnoreSettingsEntries(
      this.rootStore.commonStore.ignoreSettingsEntries + 1
    );

    socketIo.locationSocket.emit('client:snoozeNotificationsUntil', timePeriods);
  };

  @action socketEmitChangeChannelFilterPeriod = (timePeriod) => {
    this.rootStore.settingsStore.setChannelFilterPeriod(timePeriod);

    this.rootStore.commonStore.setIgnoreSettingsEntries(
      this.rootStore.commonStore.ignoreSettingsEntries + 1
    );

    socketIo.locationSocket.emit('client:changeChannelFilterPeriod', timePeriod);

    this.rootStore.commonStore.reloadChannelsAndMessages();
  };

  @action socketEmitRetrySendMessage = (message) => {
    this.rootStore.messageStore.messages.find(
      (f) => f._id === message._id
    ).SMSDeliveryStatus = this.rootStore.smsDeliveryStatusStore.smsDeliveryStatuses.find(
      (f) => f._id === utils.SMSDeliveryStatusEnum.Pending._id
    );

    socketIo.locationSocket.emit('client:retrySendMessage', message);
  };

  @action socketEmitCreateNewContact = (contact) => {
    socketIo.locationSocket.emit('client:createNewContact', contact);
  };

  @action socketEmitUpdateSeenby = (messagesForChannel) => {
    socketIo.locationSocket.emit('client:UpdateSeenBy', messagesForChannel);
  };

  @action socketEmitClientLogin = (username, password, loginToken) => {
    socketIo.loginSocket.emit('client:login', { username, password, loginToken });
  };

  @action socketEmitToggleCollapseTab = (channelId) => {
    socketIo.locationSocket.emit('client:toggleCollapseTab', channelId);
  };

  /* eslint-disable no-param-reassign */
  @action socketEmitUpdateAutoReplies = (incAction, extraValue, autoReplyCollection) => {
    let autoReplyToSend = null;

    if (incAction === 'ToggleAutoReply') {
      if (!autoReplyCollection.some((a) => a.IsSelected)) {
        autoReplyCollection.filter((f) => f.AutoReplyMessage?.trim())[0].IsSelected = true;
      }

      if (autoReplyCollection.some((s) => s.User !== null)) {
        autoReplyCollection.forEach((autoReply) => {
          // eslint-disable-next-line no-param-reassign
          autoReply.User = null;
        });
      } else {
        let selected = autoReplyCollection.find((a) => a.IsSelected);

        if (!selected?.AutoReplyMessage?.trim()) {
          selected.IsSelected = false;

          selected = autoReplyCollection.find((a) => a.AutoReplyMessage?.trim()?.length > 0);

          if (selected) {
            selected.IsSelected = true;
          }
        }

        autoReplyCollection.forEach((autoReply) => {
          // eslint-disable-next-line no-param-reassign
          autoReply.IsActive = autoReply.AutoReplyMessage || autoReply.AutoReplyMessage !== '';
          // eslint-disable-next-line no-param-reassign
          autoReply.User = autoReply.IsSelected ? this.rootStore.commonStore.getLoggedUser : null;
        });

        autoReplyToSend = autoReplyCollection.find((a) => a.User);
      }
    } else if (incAction === 'SetSelectedAutoReplyMessage' && extraValue) {
      autoReplyCollection.forEach((autoReply) => {
        // eslint-disable-next-line no-param-reassign
        autoReply.IsSelected = extraValue === autoReply._id;
        // eslint-disable-next-line no-param-reassign
        autoReply.User =
          extraValue === autoReply._id &&
          this.rootStore.autoReplyStore.autoReplies.some((s) => s.User)
            ? this.rootStore.commonStore.getLoggedUser
            : null;
      });

      autoReplyToSend = autoReplyCollection.find((a) => a.IsSelected);
    } else if (incAction === 'SetCustomAutoReplyMessage' && extraValue) {
      autoReplyCollection.forEach((autoReply) => {
        if (!autoReply.IsDefault) {
          autoReply.AutoReplyMessage = extraValue?.trim();
          autoReply.IsActive = true;
          autoReply.IsSelected = true;
          autoReply.User = autoReplyCollection.find((a) => a.User)?.User;
        } else {
          autoReply.IsSelected = false;
        }
      });

      autoReplyToSend = autoReplyCollection.find((a) => a.IsSelected);
    } else if (incAction === 'DeleteCustomAutoReplyMessage') {
      const customAutoReply = autoReplyCollection.find((a) => !a.IsDefault);
      customAutoReply.AutoReplyMessage = '';

      autoReplyCollection.forEach((autoReply) => {
        autoReply.IsSelected = false;
      });

      const randomAutoReply = autoReplyCollection.find((f) => f.AutoReplyMessage?.trim());

      randomAutoReply.IsSelected = true;
      randomAutoReply.User = customAutoReply.User || autoReplyCollection.find((a) => a.User)?.User;

      autoReplyToSend = randomAutoReply;
    }

    this.rootStore.commonStore.setIgnoreAutoReplyEntries(
      this.rootStore.commonStore.ignoreAutoReplyEntries + 1
    );

    socketIo.locationSocket.emit(
      'client:updateAutoReplies',
      autoReplyToSend,
      incAction,
      autoReplyCollection[0]?.AutoReplyType?.TypeId
    );
  };
  /* eslint-enable no-param-reassign */

  @action socketEmitLoadInitialData = (initialDataToSend) => {
    socketIo.locationSocket.emit('client:loadInitialData', initialDataToSend);
  };

  @action socketEmitUpsertCannedMessage = (cannedMessage) => {
    socketIo.locationSocket.emit('client:upsertCannedMessage', cannedMessage);
  };

  @action socketEmitDeleteCannedMessage = (cannedMessage) => {
    socketIo.locationSocket.emit('client:deleteCannedMessage', cannedMessage);
  };

  @action socketEmitUpsertAutoReply = (autoReply) => {
    socketIo.locationSocket.emit('client:upsertAutoReply', autoReply);
  };

  @action socketEmitDeleteMessage = (message) => {
    this.rootStore.messageStore.actionDeleteEntry(message);
    socketIo.locationSocket.emit('client:deleteMessage', message._id);
  };

  @action socketEmitProcessArray = (typeName, valueArray) => {
    socketIo.locationSocket.emit(
      'client:processDataByType',
      typeName,
      Array.isArray(valueArray) ? valueArray : [valueArray]
    );
  };

  @action socketEmitGetImage = (messageId) => {
    socketIo.locationSocket.emit('client:getImage', messageId);
  };

  @action socketEmitClearAllNotifications = () => {
    socketIo.locationSocket.emit('client:clearAllNotifications');
  };

  @action socketEmitAdblockDetected = () => {
    socketIo.locationSocket.emit('client:adblockDetected');
  };

  @action socketEmitBlockPhoneNumber = (phoneNumber) => {
    socketIo.locationSocket.emit('client:blockPhoneNumber', phoneNumber);
  };

  @action socketEmitUnblockPhoneNumber = (phoneNumber) => {
    socketIo.locationSocket.emit('client:unblockPhoneNumber', phoneNumber);
  };

  @action socketEmitMarkChannelAsUnread = (selectedChannelId) => {
    socketIo.locationSocket.emit('client:markChannelAsUnread', selectedChannelId);
  };

  @action socketEmitHideChannel = (selectedChannelId) => {
    socketIo.locationSocket.emit('client:hideChannel', selectedChannelId);
  };

  @action socketEmitAddPersistentNotification = (message) => {
    this.rootStore.commonStore.setIgnoreSettingsEntries(
      this.rootStore.commonStore.ignoreSettingsEntries + 1
    );

    this.rootStore.settingsStore.setAddSnackbarNotificationMessages(message);

    socketIo.locationSocket.emit('client:addPersistentNotification', message._id);
  };

  @action socketEmitRemovePersistentNotification = (message) => {
    this.rootStore.commonStore.setIgnoreSettingsEntries(
      this.rootStore.commonStore.ignoreSettingsEntries + 1
    );

    this.rootStore.settingsStore.setRemoveSnackbarNotificationMessage(message);

    socketIo.locationSocket.emit('client:removePersistentNotification', message._id);
  };

  @action socketEmitMatchContact = (phone) => {
    socketIo.locationSocket.emit('client:matchContact', phone);
  };

  @action socketEmitContactStartedTyping = (data) => {
    socketIo.locationSocket.emit('client:contactStartedTyping', data);
  };

  @action socketEmitContactStoppedTyping = (data) => {
    socketIo.locationSocket.emit('client:contactStoppedTyping', data);
  };

  @action socketWebChatEmitCloseConversation = (channelId, contactId) => {
    socketIo.locationSocket.emit('client:closeConversation', channelId, contactId);
  };

  @action socketWebChatEmitTransferConversation = (targetLocationId, channelId) => {
    socketIo.locationSocket.emit('client:transferWebChatConversation', targetLocationId, channelId);
  };

  @action socketEmitEditCustomerNames = (contactData) => {
    socketIo.locationSocket.emit('client:editCustomerNames', contactData);
  };

  @action socketEmitTransferWebChatToSMS = (data) => {
    socketIo.locationSocket.emit('client:transferWebChatToSMS', data);
  };

  @action socketEmitToggleAutoReplyIsActiveForTextChat = (data) => {
    socketIo.locationSocket.emit('client:toggleAutoReplyIsActiveForTextChat', data);
  };

  @action socketEmitToggleAutoReplyIsActiveForWebChat = (data) => {
    socketIo.locationSocket.emit('client:toggleAutoReplyIsActiveForWebChat', data);
  };

  @action socketEmitSetCustomAutoRepliesExpiration = (autoreplyId, delay) => {
    socketIo.locationSocket.emit('client:setCustomAutoRepliesExpiration', autoreplyId, delay);
  };

  @action socketEmitDeleteAutoReply = (data) => {
    this.rootStore.autoReplyV2Store.setDeleteAutoReply(data);

    socketIo.locationSocket.emit('client:deleteAutoReply', data);
  };
}

export default SendingEvents;
